import { Component, Inject, Output, Input, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { ApiService } from '@app/shared/services/api.service';
import { UtilService } from '@app/shared/services/util.service';
import { throwError } from 'rxjs';
import { URL } from '@app/shared/constants/service-urls';
import * as XLSX from 'xlsx';
import { APIRequest } from '@app/shared/services/req-res.types';
import { DownloadService } from '@app/shared/services/download.service';
import { USERS } from '@app/shared/constants/application-constants';

@Component({
    imports: [CommonModule, FormsModule, ReactiveFormsModule, MatDialogModule, MatTableModule, MatIconModule, MatButtonModule, MatProgressBarModule, MatSelectModule],
    selector: 'app-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.css']
})
export class UploadComponent implements OnInit {
  files: any[] = [];
  isError: boolean = false;
  uploading: boolean = false;
  validating: boolean = false;
  processingFile: string = '';
  rowCount: number = 0;
  errorList: string[] = [];
  uploaded: boolean = false;
  progress: number = 0;
  multisheets: boolean = false;
  processing: boolean = false
  fileStatus: 'Success' | 'Error'
  showProgress: boolean;
  successMsg: string;
  serviceError: string;
  counter: number = 0;
  counterInterval: any;
  headers: any = []
  processed: boolean = false
  geographyMaster: any = null;
  @Output() downloadSampleForUpdateEvent = new EventEmitter<void>();
  @Input() showDownloadSampleForUpdate: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<UploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { type: string, question_type:string , question:any,items: any, assessmentId?: any },
    private util: UtilService,
    private api: ApiService,
    private downloadSrvc: DownloadService
  ) {}

  ngOnInit(): void {
    // Force the dialog to use the wider width everywhere in the application
    this.dialogRef.updateSize('min(1000px, 92vw)');
    this.fetchGeographyMaster();
  }

  fetchGeographyMaster(): void {
    this.api.post('/getGeography', {}).subscribe(
      (res: any) => {
        if (res?.body?.responseBody) {
          this.geographyMaster = res.body.responseBody;
        } else if (res?.body) {
          this.geographyMaster = res.body;
        }
      },
      (err: any) => {
        console.warn('Could not load geography master data for frontend upload validation:', err);
      }
    );
  }

  get orgID(): string | null{
    const value = localStorage.getItem('OrgId');
    if (value) {
      return this.util.decrypt(value);
    }
    return null;
  }


  onDragOver(event: DragEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

onDragLeave(event: DragEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

onDrop(event: DragEvent): void {
  event.preventDefault();
  event.stopPropagation();

  const files = event.dataTransfer?.files;

  if (files && files.length > 0) {
    this.fileBrowseHandler(Array.from(files));
  }
}
  
  onFileDrop($event: any[]) {
    if (!$event || $event.length === 0) return;
    if (this.files.length === 0) {
      if (Number($event[0]?.size) > 5000000) {
        this.processFilesList($event);
      } else {
        this.serviceError = this.util.getIntlErrorMessage("UPLOAD", 'file-size-error');
        this.util.simpleDialog({
          title: 'Error',
          message: this.serviceError
        });
      }
    }
  }

  fileBrowseHandler(files: any[]) {
    if (!files || files.length === 0) {
      return;
    }
    if (Number(files[0].size) < 5000000) {
      this.processFilesList(files);
    } else {
      this.serviceError = this.util.getIntlErrorMessage("UPLOAD", 'file-size-error');
      this.util.simpleDialog({
        title: 'Error',
        message: this.serviceError
      });
    }
  }

  validateHeaders = (sheet: any, type: string, sheetIndex: any) => {
    let missingHeaders = [];
    const requiredFields = this.util.getFieldList(type);
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const headerRow = [];

    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r: 0, c })];
      headerRow.push(cell && cell.v ? String(cell.v).trim() : '');
    }
     this.headers = headerRow;
    requiredFields.forEach((item) => {
    // Only check if it's not present in the header row
    if (!headerRow.includes(item.field)) {
      console.warn(`Missing header: Expected "${item.field}" to be present in the header row.`);
      missingHeaders.push(item.field);
    }
  });
    this.progress = 10;
      const isQuestionUpload = type.toUpperCase() === 'QUESTIONS' || type.toUpperCase() === 'QUESTION_UPDATE';
    if (isQuestionUpload) {
      const seen = new Set<string>();
      const duplicates = new Set<string>();
      headerRow.forEach(h => {
        if (h) {
          if (seen.has(h)) duplicates.add(h);
          else seen.add(h);
        }
      });
      if (duplicates.size > 0) {
        this.errorList.push(
          this.multisheets
            ? `Sheet ${sheetIndex} : Duplicate column headers found: ${Array.from(duplicates).join(', ')}. Please remove duplicate columns before uploading.`
            : `Duplicate column headers found: ${Array.from(duplicates).join(', ')}. Please remove duplicate columns before uploading.`
        );
        this.scrollToBottom();
      }
    } else if (this.headers.length !== new Set([...this.headers])?.size) {
      const errorMsg = this.util.getIntlErrorMessage('UPLOAD', 'header-repeat');
      this.errorList.push(errorMsg);
    }

  if (missingHeaders.length !== 0) {
    const errorMsg = this.util.getIntlErrorMessage('UPLOAD', 'header-mismatch');
    this.errorList.push(
      this.multisheets
        ? errorMsg.replace(/^/, `Sheet ${sheetIndex} : `).replace('$1', missingHeaders?.join(', '))
        : errorMsg.replace('$1', missingHeaders?.join(', '))
    );
  }
    // if (this.headers.length !== new Set([...this.headers])?.size) {
    //   const errorMsg = this.util.getIntlErrorMessage(
    //     'UPLOAD',
    //     'header-repeat'
    //   );

    //   this.errorList.push(errorMsg);
    // }
    // if (missingHeaders.length !== 0) {
    //   const errorMsg = this.util.getIntlErrorMessage(
    //     'UPLOAD',
    //     'header-mismatch'
    //   );

    //   this.errorList.push(
    //     this.multisheets
    //       ? errorMsg
    //           .replace(/^/, `Sheet ${sheetIndex} : `)
    //           .replace('$1', missingHeaders?.join(', '))
    //       : errorMsg.replace('$1', missingHeaders?.join(', '))
    //   );
    // }
    setTimeout(() => {
      this.progress = 0;
    }, 10);
  };

  validateData = (object: any, type: string, fileIndex: string | number, sheetIndex: any) => {
    //("🚀 Starting Data Validation...");
    //("🔹 Type:", type, " | 📂 File Index:", fileIndex, " | 📑 Sheet Index:", sheetIndex);

    // Reset the error list to prevent leftover errors from previous runs
    // Convert input object to JSON format
    const jsonObj = JSON.parse(JSON.stringify(object));

    const normalizeHeader = (header: string) =>
        header ? header.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase() : '';
    if (type.toUpperCase() === "QUESTIONS" && jsonObj.length > 0) {
        const sample = jsonObj[0];
        const hasUpdateFields = ["NOS List", "NOS Element"].every(
            key => Object.keys(sample).includes(key)
        );
        if (hasUpdateFields) {
            console.log("🔁 Switching type from QUESTIONS to QUESTION_UPDATE based on field presence.");
            type = "QUESTION_UPDATE";
        }
    }

    this.errorList = [];
    //("🔄 Reset error list");

    // Retrieve required fields based on type
    const requiredFields = this.util.getFieldList(type);
    //("📋 Retrieved Required Fields:", requiredFields);
    if (!requiredFields) {
        console.error(`❌ Validation schema not found for type: ${type}`);
        this.errorList.push(`Validation schema is missing for type: ${type}. Please check your configuration.`);
        return; 
    }

       const requiredHeaders = requiredFields
                            .filter(f => f.required)
                            .map(f => normalizeHeader(f.field));                   
    //("✅ Expected Headers:", requiredHeaders);

    
    //("📂 Parsed JSON Object:", jsonObj);

    if (jsonObj.length === 0) {
        console.error("❌ The uploaded file contains no data!");
        this.errorList.push("The uploaded file contains no data.");
        return;
    }


    // Extract raw headers
    const rawHeaders = Object.keys(jsonObj[0]);
    const fileHeaders = rawHeaders.map(normalizeHeader);

    //("📋 Raw Headers from Uploaded File:", rawHeaders);
    //("📌 Normalized Headers in Uploaded File:", fileHeaders);

    // Check for missing headers
    const missingHeaders = requiredHeaders.filter(field => {
      const isMissing = !fileHeaders.includes(field);
      console.log(`Checking header "${field}":`, isMissing ? "MISSING" : "FOUND");
      return isMissing;
    });   
    if (missingHeaders.length > 0) {
        console.error("❌ Missing Headers:", missingHeaders);
        this.errorList.push(`The following headers are missing: ${missingHeaders.join(", ")}.`);
        return;
    }

    // Validate State & District values against geography master data if headers exist
    const hasStateHeader = fileHeaders.includes('state');
    const hasDistrictHeader = fileHeaders.includes('district');

    if ((hasStateHeader || hasDistrictHeader) && this.geographyMaster && typeof this.geographyMaster === 'object') {
      const normStateMap: { [key: string]: string } = {}; // normState -> canonical state
      const normDistrictMap: { [key: string]: string[] } = {}; // normState -> array of normDistricts

      Object.keys(this.geographyMaster).forEach(canonicalState => {
        const normState = canonicalState.trim().toLowerCase();
        normStateMap[normState] = canonicalState.trim();
        const distList = Array.isArray(this.geographyMaster[canonicalState]) ? this.geographyMaster[canonicalState] : [];
        normDistrictMap[normState] = distList.map((d: any) => String(d).trim().toLowerCase());
      });

      for (const [index, row] of jsonObj.entries()) {
        const normalizedRow = Object.entries(row).reduce((acc: any, [key, value]) => {
          acc[normalizeHeader(key)] = value;
          return acc;
        }, {});

        const rawState = normalizedRow['state'];
        const rawDistrict = normalizedRow['district'];

        let stateStr = rawState !== undefined && rawState !== null ? String(rawState).trim() : '';
        let districtStr = rawDistrict !== undefined && rawDistrict !== null ? String(rawDistrict).trim() : '';

        if (stateStr.toUpperCase() === 'NA' || stateStr.toUpperCase() === 'N/A') stateStr = '';
        if (districtStr.toUpperCase() === 'NA' || districtStr.toUpperCase() === 'N/A') districtStr = '';

        if (stateStr) {
          const normState = stateStr.toLowerCase();
          if (!normStateMap[normState]) {
            this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - Invalid State '${stateStr}'. State does not exist in master data.`);
            this.scrollToBottom();
            continue;
          }

          const canonicalState = normStateMap[normState];
          if (districtStr) {
            const normDist = districtStr.toLowerCase();
            const validDistList = normDistrictMap[normState] || [];
            if (!validDistList.includes(normDist)) {
              const availDistList = (this.geographyMaster[canonicalState] || []).slice(0, 6).join(', ');
              this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - District '${districtStr}' is invalid or does not belong to State '${canonicalState}'. ${availDistList ? `Available districts: ${availDistList}...` : ''}`);
              this.scrollToBottom();
              continue;
            }
          }
        } else if (districtStr) {
          this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - District '${districtStr}' was provided without a State. Please specify a valid State.`);
          this.scrollToBottom();
          continue;
        }
      }
    }

    // Iterate through required fields and validate data types
    for (const field of requiredFields) {
        //("🔹 Processing Field:", field.field);
        const normalizedFieldName = normalizeHeader(field.field);
        if (!fileHeaders.includes(normalizedFieldName)) {
          continue;
        } 

        for (const [index, row] of jsonObj.entries()) {
            
            // Normalize keys in the row for reliable access
            const normalizedRow = Object.entries(row).reduce((acc, [key, value]) => {
              acc[normalizeHeader(key)] = value;
              return acc;
            }, {});

            let fieldValue = normalizedRow[normalizeHeader(field.field)];

            let valStr = fieldValue !== undefined && fieldValue !== null ? String(fieldValue).trim() : '';
            if (valStr.toUpperCase() === 'NA' || valStr.toUpperCase() === 'N/A') {
                valStr = '';
            }
            const isValueEmpty = valStr === '';

            // Conditional validation for CUSTOMER_MASTER / LEAD_MASTER based on "Is Pin Code Applicable"
            if (type.toUpperCase() === 'CUSTOMER_MASTER' || type.toUpperCase() === 'LEAD_MASTER') {
                const pinApplicableRaw = normalizedRow[normalizeHeader('Is Pin Code Applicable')];
                // Treat blank/null/undefined as 'yes' (the default dropdown value)
                const pinApplicable = (String(pinApplicableRaw ?? '').trim().toLowerCase()) || 'yes';

                if (field.field === 'Pincode') {
                    if (pinApplicable === 'no') {
                        continue; // Pincode not needed when No
                    }
                    if (isValueEmpty) {
                        this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - Pincode is required when 'Is Pin Code Applicable' is Yes`);
                        this.scrollToBottom();
                    }
                    continue;
                }

                if (field.field === 'State' || field.field === 'District') {
                    if (pinApplicable !== 'no') {
                        continue; // State/District not needed unless explicitly No
                    }
                    if (isValueEmpty) {
                        this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - ${field.field} is required when 'Is Pin Code Applicable' is No`);
                        this.scrollToBottom();
                    }
                    continue;
                }
            }

            if (isValueEmpty) {
                if (field.required) {
                    console.error(`❗ Missing Required Field - ${field.field} in Row ${index + 1}`);
                    this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - Missing required field: ${field.field}`);
                    this.scrollToBottom();
                }
                continue;
            }

            // Validate Data Type
            if (field.datatype) {
                let actualType: string;
                if (!isNaN(Number(valStr))) {
                    const numVal = Number(valStr);
                    actualType = Number.isInteger(numVal) ? "integer" : "float";
                } else {
                    actualType = "text";
                }

                const expectedType = String(field.datatype).toLowerCase().trim();
                let typeMatch = false;

                if (['text', 'string'].includes(expectedType)) {
                    typeMatch = true;
                } else if (['number', 'float', 'integer'].includes(expectedType)) {
                    if (['integer', 'float'].includes(actualType)) {
                        typeMatch = true;
                    }
                } else if (expectedType === actualType) {
                    typeMatch = true;
                }

                if (!typeMatch) {
                    this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - Expected ${field.datatype}, but found ${actualType} for ${field.field}.`);
                    this.scrollToBottom();
                    continue; 
                }
            }

            // Validate Regex Pattern (if applicable)
            if (field.regex && field.regex !== '') {
                const regexPattern = typeof field.regex === 'string' ? new RegExp(field.regex) : field.regex;
                if (!regexPattern.test(valStr)) {
                    this.errorList.push(`Sheet ${sheetIndex} : Row ${index + 1} - Invalid format for ${field.field}.`);
                    this.scrollToBottom();
                    continue;
                }
            }

            // Progress update for UI feedback
            const rowProgress = 80 / this.rowCount;
            this.progress = Math.min(100, (this.files[fileIndex]?.progress || 0) + rowProgress);
            //(`📊 Updated Progress: ${this.progress}`);
        }
    }

    //("📌 Final Error List:", this.errorList);
    //("🔍 Debugging Upload Button Condition:");
    //("📌 uploaded:", this.uploaded);
    //("📌 errorList.length:", this.errorList.length);
    //("📌 files.length (Are there files?):", this.files.length);
    //("📌 processing:", this.processing);
    //("📌 fileStatus:", this.fileStatus);
    //("✅ Data Validation Completed.");
};





scrollToBottom() {
  setTimeout(() => {
    const logger = document.querySelector('.logger');
    if (logger) {
      logger.scrollTop = logger.scrollHeight;
    }
  }, 0);
}




  startTimer() {
    this.counterInterval = setInterval(() => {
        this.counter++
    },1000)
  }

  parseExcel(file: Blob, type: string, fileIndex: number) {
    let isError = false;
    var reader = new FileReader();
    reader.onload = (e) => {
      var data = (<any>e.target).result;
      var workbook = XLSX.read(data, {
        type: 'binary',
      });
      this.progress = 5;
      this.multisheets = workbook.SheetNames?.length > 1;
    if (type.toUpperCase() === 'QUESTIONS' || type.toUpperCase() === 'QUESTION_PAPERS') {
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const XL_row_object = XLSX.utils.sheet_to_json(worksheet);

      this.rowCount = XL_row_object.length;
      if (this.rowCount === 0) {
        const errorMsg = this.util.getIntlErrorMessage('UPLOAD', 'empty-records');
        this.errorList.push(errorMsg);
      }

      this.validateHeaders(worksheet, type, 1);

      setTimeout(() => {
        if (this.errorList.length > 0) {
          this.processed = true;
          return;
        }
        try {
          this.validateData(XL_row_object, type, fileIndex, 1);
        } catch (error) {
          console.error("Data validation error:", error);
          this.errorList.push("An unexpected error occurred during data validation.");
        } finally {
          this.processed = true;
        }
      }, 1000);
    }
    else {
      workbook.SheetNames.every(
        async function (sheetName, index) {
          const XL_row_object = XLSX.utils.sheet_to_json(
            workbook.Sheets[sheetName]
          );
          this.progress = 5;
          this.rowCount = XL_row_object.length;
          if (this.rowCount === 0) {
            const errorMsg = this.util.getIntlErrorMessage(
              'UPLOAD',
              'empty-records'
            );
            this.errorList.push(
              this.multisheets
                ? errorMsg.replace(/^/, `Sheet ${index + 1} : `)
                : errorMsg
            );
          }
          await this.validateHeaders(workbook.Sheets[sheetName], type, index+1);

          setTimeout(() => {
            try {
              this.validateData(XL_row_object, type, fileIndex, index+1);
            } catch (error) {
              console.error(`Data validation error on Sheet ${index + 1}:`, error);
              this.errorList.push(`Sheet ${index + 1} : An unexpected error occurred during data validation.`);
            } finally {
              this.processed = true;
            }
          }, 1000);
          return true;
        }.bind(this),
        this
      );
    }
    };

    reader.onerror = function (ex) {
      return throwError(
        'There was an error while trying to read the file. Please try again later.'
      );
    };
    reader.readAsBinaryString(file);

    return isError;
  }

  async uploadFilesSimulator(index: number) {
    setTimeout(() => {
      this.uploading = true;
      setTimeout(() => {
        this.validating = true;
        if (index === this.files.length) {
          return;
        } else {
          this.parseExcel(this.files[0], this.data.type, index);
          const progressInterval = setInterval(() => {
            if (this.files[index].progress >= 100 || this.processed) {
              this.files[index].progress = 100;
              clearInterval(progressInterval);
              clearInterval(this.counterInterval);
              this.uploaded = true;
              this.processing = false;
              this.fileStatus = this.errorList?.length > 0 ? 'Error': 'Success';
              this.uploadFilesSimulator(index + 1);
            } else {
              this.files[index].progress += this.progress;
            }
          }, 5);
        }
      }, 1000);
    }, 1000);
  }

  processFilesList(files: Array<any>) {
    this.startTimer()
    this.errorList = [];
    this.rowCount = 0;
    this.progress = 0;
    this.uploaded = false;
    this.processing = false;
    this.multisheets = false;

    for (const item of files) {
      item.progress = 0;
      this.files.push(item);
    }
    this.processing = true
    this.processingFile = this.files[0]?.name;
    this.uploadFilesSimulator(0);
  }

  formatBytes(bytes: number, decimals: number) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  downloadLogs() {
    let downloadText = {
      "File name": this.files[0].name,
      "File size": this.files[0].size,
      "Processing time": `${this.counter}s`,
      "Processed headers": this.headers.join(','),
      "Error list": this.errorList.join('\n')
    }
    let downloadLink = document.createElement('a');
    downloadLink.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(JSON.stringify(downloadText))}`);
    downloadLink.setAttribute('download', `${this.files[0].name.replace(/.xlsx/g, '')}_errors.txt`);
    document.body.appendChild(downloadLink);
    downloadLink.click(); 
  }
 
  uploadFile() {
    this.showProgress = true
    let formdata = new FormData();
    formdata.append('file', this.files[0]);
    let resp;
    if (!this.data.items) {
      const assessmentParam = this.data.assessmentId ? `&assessmentId=${this.data.assessmentId}` : '';
      resp = this.api.post(`${URL.ADMIN.UPLOAD_FILE}?tableName=${this.data.type}&orgId=${this.orgID}&type=formdata&question_type=${this.data.question_type}&questionBank=${this.data.question?.question_bank_title}&difficulityLevel=${this.data.question?.difficulty_level_name}&questionCategory=${this.data.question?.question_category}&questionId=${this.data.question?.question_id}${assessmentParam}`, formdata)
    } else {
      if (this.data.type === USERS) {
        resp = this.api.post(`${URL.ADMIN.UPLOAD_FILE}?tableName=${this.data.type}&userRoleId=${this.data.items.roleId}&userCategoryId=${this.data.items.categoryId}&type=formdata`, formdata)
      }
    }
    
    resp.subscribe(
      (data: any) => {
        console.log('Upload response:', data);
        this.showProgress = false;

        if (data?.body?.async) {
          const typeLabel = this.data.type === 'questions' ? 'questions' : this.data.type === 'candidates' ? 'candidate' : 'data';
          this.util.simpleDialog({
            title: 'Upload started',
            message: `Your ${typeLabel} file is being processed in the background. You will receive a notification and an email with the upload report once it completes.`,
          });
        } else {
          const resBody = data?.body?.responseBody || data?.body || {};
          const totalErrors = resBody.total_errors || resBody.error?.total_errors || 0;
          const warningText = resBody.warning || resBody.error?.first_error;
          const messageText = resBody.message || resBody.title || '';

          if (totalErrors > 0 || warningText || (resBody.error && Object.keys(resBody.error).length > 0)) {
            const displayTitle = totalErrors > 0 ? 'Upload Warning / Validation Alert' : 'Updated';
            const displayMessage = messageText || warningText || 'Some rows failed validation.';
            this.util.simpleDialog({
              title: displayTitle,
              message: displayMessage,
            });
          } else {
            this.successMsg = messageText || this.util.getSuccessMessage("BULK_UPLOAD", data.body.responseHead.statusCode, data.body.responseHead.statusDesc);
            this.util.simpleDialog({
              title: 'Updated',
              message: this.successMsg,
            });
          }
        }
        this.files = [];
        this.dialogRef.close(data);
      },
      (err: any) => {
        this.showProgress = false;
        if (err.responseHead) {
          this.serviceError = this.util.getIntlErrorMessage("BULK_UPLOAD", err.responseHead.statusCode, err.responseHead.statusDesc);
          this.util.simpleDialog({
            title: 'Error',
            message: this.serviceError
          });
        }
      }
    );
  }

  downloadSample() {
    this.downloadSrvc.downloadSampleFile(this.data).subscribe((data: any) => {
      const blobData = data.body ? data.body : data;
      let binaryData = [];
      binaryData.push(blobData);
      let downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(
        new Blob(binaryData, { type: 'blob' })
      );
      downloadLink.setAttribute(
        'download',
        `${this.data.type}_sample.xlsx`
      );
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    },
      (error: any) => {
        console.error("Error downloading sample file: ", error);
        this.util.simpleDialog({
          title: 'Error',
          message: 'Failed to download the sample file. Please try again or contact support.'
        });
      });
  }

  downloadSampleForUpdate() {
    this.downloadSampleForUpdateEvent.emit();
  }
}
